import * as Base from "@base-ui/react"
import * as React from "react"

import { documentTypes, videoTypes } from "../../api/lib/upload.ts"
import type { Row } from "../../generated/schema.ts"
import { useThreadAccounts } from "../hooks/useThreadAccounts.ts"
import { useTimeAgo } from "../hooks/useTimeAgo.ts"
import * as ImageUtils from "../lib/image.ts"
import { rpc } from "../lib/rpc.ts"
import { Markdown } from "./Markdown.tsx"
import { MessageEditor } from "./MessageEditor.tsx"
import { ReactionPicker } from "./ReactionPicker.tsx"

export function Message(
  props: React.PropsWithChildren<{
    account: Pick<Row.account, "avatar_url" | "last_seen_at" | "login" | "name">
    edited: boolean | undefined
    editing: boolean | undefined
    join: boolean | undefined
    message: Pick<
      Row.message,
      "channel_id" | "content" | "deleted_at" | "id" | "inserted_at"
    >
    repositoryId: string
    repositoryOwner: Pick<Row.account | Row.organization, "id" | "login">
    onEdit: MessageEditor.Props["onSubmit"]
    onCancelEdit: () => void
  }>,
) {
  const {
    account,
    children,
    edited,
    editing,
    join,
    message,
    repositoryId,
    repositoryOwner,
    onCancelEdit,
    onEdit,
  } = props

  const time = React.useMemo(
    () =>
      new Date(message.inserted_at).toLocaleTimeString(undefined, {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }),
    [message.inserted_at],
  )

  const active = React.useMemo(
    () =>
      account?.last_seen_at &&
      new Date(account.last_seen_at).getTime() > Date.now() - 5 * 60 * 1000,
    [account],
  )

  if (message.deleted_at)
    return (
      <div className="flex w-full gap-3">
        {join ? (
          <div className="flex min-w-9 justify-end" />
        ) : (
          <div className="mt-1 size-9 select-none rounded-lg text-transparent" />
        )}

        <div className="flex w-[50%] flex-grow flex-col gap-0.5">
          <div className="select-text text-[15px] text-neutral-10 leading-[22px] dark:text-neutral-9">
            message deleted
          </div>

          {children && <div className="flex flex-col gap-1">{children}</div>}
        </div>
      </div>
    )

  return (
    <div className="flex w-full gap-3">
      {join ? (
        <div className="flex min-w-9 justify-end">
          <div className="mt-[3px] hidden select-text text-neutral-10 text-xs leading-normal group-hover:block group-data-editing:hidden dark:text-neutral-9">
            {time}
          </div>
        </div>
      ) : (
        <div className="relative h-fit">
          <img
            alt={account.login}
            className="mt-1 size-9 select-none rounded-lg bg-neutral-4 text-transparent"
            src={account.avatar_url}
          />
          {active && (
            <div className="absolute -right-[2px] -bottom-[2px] size-2 rounded-full bg-green-4 outline-2 outline-[var(--background-color-default)]" />
          )}
        </div>
      )}

      <div className="flex w-[50%] flex-grow flex-col gap-0.5">
        {editing ? (
          <MessageEditor
            channelId={message.channel_id}
            defaultValue={message.content}
            onEscape={onCancelEdit}
            onSubmit={onEdit}
            placeholder="Edit message"
            repositoryId={repositoryId}
            state={editing ? "edit" : "create"}
          />
        ) : (
          <>
            {!join && (
              <div className="flex items-baseline gap-2">
                <div className="select-text font-semibold text-[15px] leading-[22px]">
                  {account.name ?? account.login}
                </div>
                <div className="select-text text-neutral-10 text-xs dark:text-neutral-9">
                  {time}
                </div>
              </div>
            )}

            <div className="message-content select-text text-[15px] text-neutral-12 leading-[22px] dark:text-neutral-11 [&>p+p]:mt-4 group-data-edited:[&>p:last-of-type]:me-1 group-data-edited:[&>p:last-of-type]:inline">
              <Markdown
                content={message.content}
                repositoryId={repositoryId}
                repositoryOwner={repositoryOwner}
              />
              {edited && (
                <span className="text-neutral-9 text-xs dark:text-neutral-8">
                  (edited)
                </span>
              )}
            </div>

            {children && (
              <div className="flex flex-col gap-1 empty:hidden">{children}</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export namespace Message {
  export const Attachments = React.memo(Attachments_)
  export function Attachments_(props: {
    attachments: readonly Row.attachment[]
  }) {
    const { attachments } = props
    return (
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment) => {
          // TODO: Render document file types
          if (documentTypes.includes(attachment.type as never))
            return <div key={attachment.id}>document: {attachment.name}</div>
          if (videoTypes.includes(attachment.type as never))
            return (
              <Video
                blurhash={attachment.blurhash}
                height={attachment.height}
                key={attachment.id}
                name={attachment.name}
                object_key={attachment.key}
                type={attachment.type}
                width={attachment.width}
              />
            )
          return (
            <Image
              blurhash={attachment.blurhash}
              height={attachment.height}
              key={attachment.id}
              name={attachment.name}
              object_key={attachment.key}
              type={attachment.type}
              width={attachment.width}
            />
          )
        })}
      </div>
    )
  }

  export function ReactionBar(props: {
    currentAccountId: Row.account["id"] | undefined
    reactions: readonly Row.reaction[]
    onReact: Parameters<typeof Reaction>[0]["onReact"]
  }) {
    const { currentAccountId, reactions, onReact } = props

    const reactionsWithCounts = React.useMemo(() => {
      if (!reactions) return []

      const tallied = []
      const grouped = Object.groupBy(reactions, (reaction) => reaction.emoji)
      for (const [key, values] of Object.entries(grouped)) {
        if (!values?.length) continue
        tallied.push({
          count: values.length,
          emoji: key,
          inserted_at: Math.min(
            ...values.map((reaction) => reaction.inserted_at),
          ),
          reacted: currentAccountId
            ? values.some(
                (reaction) => reaction.account_id === currentAccountId,
              )
            : false,
        })
      }
      return tallied.toSorted((a, b) => {
        // sort by count (descending)
        if (a.count !== b.count) return b.count - a.count
        // then by inserted_at (ascending)
        return a.inserted_at - b.inserted_at
      })
    }, [currentAccountId, reactions])

    return (
      <Base.Toolbar.Root className="flex flex-wrap gap-1">
        {reactionsWithCounts.map((reaction) => (
          <Reaction
            count={reaction.count}
            emoji={reaction.emoji}
            key={reaction.emoji}
            onReact={onReact}
            reacted={reaction.reacted}
          />
        ))}

        {currentAccountId && (
          <ReactionPicker
            menuButton={
              <Base.Toolbar.Button className="flex size-[28px] items-center justify-center rounded-full border border-button-default bg-button-default-rest text-button-default/70 hover:bg-button-default-hover data-reacted:border-accent">
                <GitchatReactionAdd className="size-4" />
              </Base.Toolbar.Button>
            }
            onSelect={onReact}
          />
        )}
      </Base.Toolbar.Root>
    )
  }

  export function ThreadBar(props: { thread: Row.thread }) {
    const { thread } = props

    const maxAvatars = 5
    const overflowAccountCount = thread.account_count - maxAvatars
    const threadAccounts = useThreadAccounts({ thread })
    const accounts = React.useMemo(
      () => threadAccounts.slice(0, maxAvatars),
      [threadAccounts],
    )

    const timeAgo = useTimeAgo(thread.last_message_at)

    return (
      <div className="@container/thread group/thread relative -ms-1 flex items-center justify-between gap-4 rounded-md border border-transparent p-1 hover:border-default hover:bg-default">
        <div className="flex items-center gap-2">
          <div className="flex min-h-6 min-w-6 gap-1">
            {accounts.map((account, i) => (
              <div
                className="relative size-6 overflow-hidden rounded-md"
                key={account.id}
              >
                <img
                  alt={`${account.login} avatar url`}
                  src={account.avatar_url}
                />
                {overflowAccountCount && i === maxAvatars - 1 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 font-medium text-[14px] text-white leading-none">
                    {overflowAccountCount > 9
                      ? "9+"
                      : `+${overflowAccountCount}`}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="font-medium text-[13px] text-accent tabular-nums leading-none">
            {thread.message_count}{" "}
            {thread.message_count === 1 ? "reply" : "replies"}
          </div>
          <div className="@3xs/thread:block hidden font-medium text-[13px] text-neutral-10 tabular-nums leading-none dark:text-neutral-9">
            Last reply {timeAgo}
          </div>
        </div>

        <div className="absolute end-1 flex items-center">
          <OcticonChevronRight16 className="size-4 text-neutral-7 opacity-0 group-hover/thread:opacity-100 dark:text-neutral-8" />
        </div>
      </div>
    )
  }
}

/////////////////////////////////////////////////////////////////////////////////
// Internal

function useThumbnailDimensions(options: {
  width: number | null
  height: number | null
}) {
  const { width, height } = options
  return React.useMemo(() => {
    if (!width || !height) return { aspectRatio: 1 }

    const maxSize = 320
    const scale = Math.min(maxSize / width, maxSize / height, 1)

    return {
      width: Math.round(width * scale),
      height: Math.round(height * scale),
      aspectRatio: width / height,
    }
  }, [width, height])
}

function useUrl(object_key: string) {
  return React.useMemo(() => {
    const parts = object_key.split("/")
    return rpc.attachments[":repository_id"][":channel_id"][":upload_id"]
      .$url({
        param: {
          repository_id: parts[0]!,
          channel_id: parts[1]!,
          upload_id: parts[2]!,
        },
      })
      .toString()
  }, [object_key])
}

export function Image(
  props: Pick<
    Row.attachment,
    "blurhash" | "height" | "name" | "type" | "width"
  > & {
    object_key: string
  },
) {
  const { blurhash, height, name, object_key, width } = props

  const thumbnailRef = React.useRef<HTMLImageElement>(null)
  const [loaded, setLoaded] = React.useState(() => {
    const thumbnail = thumbnailRef.current
    return thumbnail?.complete && thumbnail.naturalWidth > 0
  })

  const url = useUrl(object_key)

  const { data: blurhashDataUrl } = ImageUtils.useFromBlurhash(blurhash, {
    height,
    width,
  })

  const thumbnailDimensions = useThumbnailDimensions({ width, height })

  return (
    <div className="flex flex-col gap-1">
      <div
        className="w-full rounded-lg bg-center bg-cover bg-muted"
        style={
          blurhashDataUrl
            ? {
                backgroundImage: `url(${blurhashDataUrl})`,
                aspectRatio: thumbnailDimensions.aspectRatio,
              }
            : {}
        }
      >
        <img
          alt={loaded ? name : ""}
          className="rounded-lg object-cover transition-opacity duration-150 ease-in-out data-[loaded=false]:opacity-0"
          data-loaded={loaded}
          height={thumbnailDimensions.height}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          ref={thumbnailRef}
          src={url}
          width={thumbnailDimensions.width}
        />
      </div>
    </div>
  )
}

export function Video(
  props: Pick<
    Row.attachment,
    "blurhash" | "height" | "name" | "type" | "width"
  > & {
    object_key: string
  },
) {
  const { blurhash, height, object_key, width } = props

  const videoRef = React.useRef<HTMLVideoElement>(null)

  const url = useUrl(object_key)

  const { data: blurhashDataUrl } = ImageUtils.useFromBlurhash(blurhash, {
    height,
    width,
  })

  const thumbnailDimensions = useThumbnailDimensions({ width, height })

  return (
    <div className="flex flex-col gap-1">
      <div
        className="group/video relative w-full rounded-lg bg-center bg-cover bg-muted"
        style={{
          backgroundImage: `url(${blurhashDataUrl})`,
          aspectRatio: thumbnailDimensions.aspectRatio,
        }}
      >
        <div
          style={{
            height: thumbnailDimensions.height,
            maxWidth: thumbnailDimensions.width,
          }}
        >
          {/* biome-ignore lint/a11y/useMediaCaption: _ */}
          <video
            className="h-full w-full rounded-lg object-cover transition-opacity duration-150 ease-in-out data-[loaded=false]:opacity-0"
            controls
            height={thumbnailDimensions.height}
            playsInline
            preload="metadata"
            ref={videoRef}
            src={url}
            width={thumbnailDimensions.width}
          />
        </div>
      </div>
    </div>
  )
}

function Reaction(props: {
  count: number
  emoji: string
  reacted: boolean
  onReact: (value: Row.reaction["emoji"]) => Promise<void>
}) {
  const { count, emoji, reacted, onReact } = props
  return (
    <Base.Toolbar.Button
      {...(reacted ? { "data-reacted": true } : {})}
      className="flex h-[28px] items-center justify-center gap-1 rounded-full border border-button-default bg-button-default-rest px-2 text-[12px] leading-none hover:bg-accent/40 data-reacted:border-accent"
      onClick={() => onReact(emoji)}
    >
      <div>{emoji}</div>
      <div className="font-medium text-button-invisible tabular-nums">
        {count}
      </div>
    </Base.Toolbar.Button>
  )
}
